// Lightweight synth-based audio manager for the Austen garden game.
// A single AudioContext is created lazily on the first real user gesture and
// stored on window so it survives React StrictMode remounts and Phaser scene
// restarts. It is NEVER closed (a web page has no real "termination"), which
// keeps us clear of the "Cannot suspend/resume a closed AudioContext" errors.
// If audio cannot start for any reason, every method is a safe no-op and the
// game keeps running normally.

const MUTE_KEY = "austen_muted";

function createManager() {
  const mgr = {
    ctx: null,
    master: null,
    musicGain: null,
    sfxGain: null,
    unlocked: false,
    muted: (() => {
      try { return localStorage.getItem(MUTE_KEY) === "1"; } catch { return false; }
    })(),
    _schedTimer: null,
    _nextNoteTime: 0,
    _step: 0,
    _lastFoot: 0,
  };

  const safe = (fn) => { try { return fn(); } catch { return null; } };

  mgr._ensureContext = function () {
    if (this.ctx && this.ctx.state !== "closed") return this.ctx;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    this.ctx = safe(() => new AC());
    if (!this.ctx) return null;
    this.master = this.ctx.createGain();
    this.master.gain.value = 0.9;
    this.master.connect(this.ctx.destination);
    this.musicGain = this.ctx.createGain();
    this.musicGain.gain.value = this.muted ? 0 : 0.11;
    this.musicGain.connect(this.master);
    this.sfxGain = this.ctx.createGain();
    this.sfxGain.gain.value = this.muted ? 0 : 0.28;
    this.sfxGain.connect(this.master);
    return this.ctx;
  };

  // Play a 1-sample silent buffer inside the gesture — required to fully
  // unlock Web Audio on iOS Safari.
  mgr._playSilent = function () {
    if (!this.ctx) return;
    safe(() => {
      const b = this.ctx.createBuffer(1, 1, 22050);
      const s = this.ctx.createBufferSource();
      s.buffer = b;
      s.connect(this.ctx.destination);
      s.start(0);
    });
  };

  // Call DIRECTLY inside a user gesture (tap / click / key). Creates or reuses
  // a valid AudioContext, resumes it in-gesture, unlocks iOS and starts music.
  mgr.unlock = function () {
    const ctx = this._ensureContext();
    if (!ctx) return;
    if (ctx.state === "suspended") safe(() => ctx.resume());
    this._playSilent();
    this.needsGesture = false;
    if (!this.unlocked) {
      this.unlocked = true;
      if (!this.muted) this.startMusic();
    } else if (!this.muted && !this._schedTimer) {
      this.startMusic();
    }
  };

  mgr.setMuted = function (m) {
    this.muted = m;
    try { localStorage.setItem(MUTE_KEY, m ? "1" : "0"); } catch {}
    if (this.ctx && this.ctx.state !== "closed") {
      const t = this.ctx.currentTime;
      safe(() => this.musicGain.gain.setTargetAtTime(m ? 0 : 0.11, t, 0.05));
      safe(() => this.sfxGain.gain.setTargetAtTime(m ? 0 : 0.28, t, 0.05));
    }
    if (m) {
      this.stopMusic();
    } else if (this.unlocked && !this._schedTimer) {
      this.startMusic();
    }
    return this.muted;
  };
  mgr.toggleMuted = function () { return this.setMuted(!this.muted); };
  mgr.isMuted = function () { return this.muted; };

  // ---- one-shot synth voices ----
  mgr._voice = function (dest, freq, start, dur, type, peak, freqEnd) {
    if (!this.ctx) return;
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.type = type;
    o.frequency.setValueAtTime(freq, start);
    if (freqEnd) safe(() => o.frequency.exponentialRampToValueAtTime(freqEnd, start + dur));
    g.gain.setValueAtTime(0.0001, start);
    g.gain.exponentialRampToValueAtTime(peak, start + 0.012);
    g.gain.exponentialRampToValueAtTime(0.0001, start + dur);
    o.connect(g);
    g.connect(dest);
    o.start(start);
    o.stop(start + dur + 0.02);
  };

  // ---- Regency-style looping chamber music (procedural) ----
  // I-V-vi-IV in G major, gentle harpsichord-like plucks with a soft bass.
  const PROG = [
    { bass: 98.0,  notes: [392.0, 493.9, 587.3] }, // G  (G3, B4, D5)
    { bass: 73.4,  notes: [369.99, 440.0, 587.3] }, // D  (D..)
    { bass: 82.4,  notes: [329.6, 392.0, 493.9] }, // Em (E..)
    { bass: 65.4,  notes: [349.2, 440.0, 523.3] }, // C  (C..)
  ];
  const BPM = 92;

  mgr._scheduleStep = function () {
    if (!this.ctx || this.muted === undefined) return;
    const spb = 60 / BPM;         // seconds per beat
    const stepDur = spb / 2;      // eighth notes
    const chordIndex = Math.floor(this._step / 8) % PROG.length;
    const beatInChord = this._step % 8;
    const chord = PROG[chordIndex];
    const t = this._nextNoteTime;

    // bass on chord downbeat and halfway
    if (beatInChord === 0 || beatInChord === 4) {
      this._voice(this.musicGain, chord.bass, t, 0.9, "triangle", 0.5);
    }
    // arpeggio pluck
    const arp = chord.notes[beatInChord % chord.notes.length];
    this._voice(this.musicGain, arp, t, 0.55, "triangle", 0.32);
    // occasional sparkle octave
    if (beatInChord === 2 || beatInChord === 6) {
      this._voice(this.musicGain, arp * 2, t, 0.4, "sine", 0.12);
    }

    this._nextNoteTime += stepDur;
    this._step += 1;
  };

  mgr.startMusic = function () {
    if (!this.ctx || this._schedTimer) return;
    this._step = 0;
    this._nextNoteTime = this.ctx.currentTime + 0.1;
    const lookahead = 0.15;
    this._schedTimer = setInterval(() => {
      if (!this.ctx || this.ctx.state === "closed") return;
      while (this._nextNoteTime < this.ctx.currentTime + lookahead) {
        this._scheduleStep();
      }
    }, 40);
  };

  mgr.stopMusic = function () {
    if (this._schedTimer) { clearInterval(this._schedTimer); this._schedTimer = null; }
  };

  // ---- SFX ----
  mgr.footstep = function () {
    if (!this.ctx || this.ctx.state === "closed" || !this.unlocked) return;
    const now = this.ctx.currentTime;
    if (now - this._lastFoot < 0.26) return; // throttle
    this._lastFoot = now;
    this._voice(this.sfxGain, 150, now, 0.09, "triangle", 0.25, 90);
  };

  mgr.dialogue = function () {
    if (!this.ctx || this.ctx.state === "closed") return;
    const t = this.ctx.currentTime;
    this._voice(this.sfxGain, 659.3, t, 0.14, "sine", 0.22);
    this._voice(this.sfxGain, 880.0, t + 0.09, 0.16, "sine", 0.2);
  };

  mgr.pickup = function () {
    if (!this.ctx || this.ctx.state === "closed") return;
    const t = this.ctx.currentTime;
    [523.3, 659.3, 784.0, 1046.5].forEach((f, i) => {
      this._voice(this.sfxGain, f, t + i * 0.075, 0.22, "triangle", 0.26);
    });
  };

  mgr.success = function () {
    if (!this.ctx || this.ctx.state === "closed") return;
    const t = this.ctx.currentTime;
    // resolved major chord flourish
    [392.0, 493.9, 587.3, 784.0].forEach((f, i) => {
      this._voice(this.sfxGain, f, t + i * 0.06, 0.6, "triangle", 0.24);
    });
  };

  return mgr;
}

const audio =
  (typeof window !== "undefined" && window.__austenAudio) ||
  createManager();

if (typeof window !== "undefined") {
  window.__austenAudio = audio;
  audio.needsGesture = audio.needsGesture || false;
  audio.onNeedGesture = audio.onNeedGesture || null;
  // When returning to the tab, try to resume WITHOUT closing the context.
  // If the browser refuses (needs a new gesture), flag it so the UI can ask.
  if (!audio._visBound) {
    audio._visBound = true;
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState !== "visible") return;
      const ctx = audio.ctx;
      if (ctx && ctx.state === "suspended") {
        try {
          ctx.resume().catch(() => {
            audio.needsGesture = true;
            if (audio.onNeedGesture) audio.onNeedGesture();
          });
        } catch {
          audio.needsGesture = true;
          if (audio.onNeedGesture) audio.onNeedGesture();
        }
      }
    });
  }
}

export default audio;
